import { z } from "zod";

/**
 * A Dataverse environment host: `<org>.crm.dynamics.com` or a regional variant
 * (`crm4`, `crm11`, …). Anchored at both ends, mirroring the backend's
 * `normalizeMsdynamicsEnvironmentUrl` — an unanchored match would accept lookalikes
 * such as `foo.dynamics.com.attacker.net`.
 *
 * The backend is the authority (it re-validates before signing the OAuth state token);
 * this copy exists so the user sees the error in the field instead of after a round-trip.
 */
const ENVIRONMENT_HOST_PATTERN = /^(?:[a-z0-9][a-z0-9-]*\.)+crm\d*\.dynamics\.com$/;

/** The URL must be an origin only: https, no credentials, no port, no path/query/hash. */
const isBareHttpsOrigin = (parsed: URL): boolean =>
	parsed.protocol === "https:" &&
	parsed.username === "" &&
	parsed.password === "" &&
	parsed.port === "" &&
	(parsed.pathname === "" || parsed.pathname === "/") &&
	parsed.search === "" &&
	parsed.hash === "";

const parseUrl = (value: string): URL | null => {
	try {
		return new URL(value);
	} catch {
		return null;
	}
};

/**
 * Validate and normalise a customer-supplied Dataverse environment URL exactly the way
 * the backend does. Returns the bare `https://<host>` origin, or `null` when the value
 * is not a Dynamics 365 environment URL.
 */
export const normalizeDynamicsEnvironmentUrl = (raw: string | null | undefined): string | null => {
	if (typeof raw !== "string" || raw.trim().length === 0) {
		return null;
	}

	const parsed = parseUrl(raw.trim().replace(/\/+$/, ""));

	if (!parsed || !isBareHttpsOrigin(parsed) || !ENVIRONMENT_HOST_PATTERN.test(parsed.hostname)) {
		return null;
	}

	return `https://${parsed.hostname}`;
};

export const isValidDynamicsEnvironmentUrl = (raw: string | null | undefined): boolean =>
	normalizeDynamicsEnvironmentUrl(raw) !== null;

/**
 * Form schema of the connection tab's environment-URL field. The message is injected so
 * the component can supply a translated string (§10) while the rule stays here.
 */
export const createDynamicsConnectionSchema = (invalidMessage: string) =>
	z.object({
		environmentUrl: z
			.string()
			.trim()
			.refine(isValidDynamicsEnvironmentUrl, { message: invalidMessage }),
	});

export type DynamicsConnectionFormValues = z.infer<
	ReturnType<typeof createDynamicsConnectionSchema>
>;
